import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./styles.css";
import API_BASE from "../api";
import AnomalyMap from "./AnomalyMap";
import WarmingStripes from "./WarmingStripes";

// The visitor's approximate city (from their IP), looked up once per session.
// Offered as a shortcut under the search box; the landing page never redirects.
let nearbyRequest = null;
function loadNearby() {
  nearbyRequest ??= fetch(`${API_BASE}/geolocate`)
    .then((r) => (r.ok ? r.json() : null))
    .then((data) => (data?.city_id || data?.station_id ? data : null))
    .catch(() => null);
  return nearbyRequest;
}

function HowMuchHotter() {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [error, setError] = useState(null);
  const [nearby, setNearby] = useState(null);
  const debounceRef = useRef(null);
  const listRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    let live = true;
    loadNearby().then((city) => live && setNearby(city));
    return () => {
      live = false;
    };
  }, []);

  // Fetch suggestions with debounce
  useEffect(() => {
    if (query.length < 2) {
      setSuggestions([]);
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API_BASE}/search?q=${encodeURIComponent(query)}`);
        const data = await res.json();
        setSuggestions(data);
        setActiveIndex(-1);
      } catch {
        setSuggestions([]);
      }
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const loadCity = async (cityData) => {
    setError(null);
    setSuggestions([]);
    try {
      const dataResponse = await fetch(`${API_BASE}/get-data`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // nClimGrid backends use city_id; station backends use station_id.
        body: JSON.stringify({ city_id: cityData.city_id, station_id: cityData.station_id }),
      });

      if (!dataResponse.ok) {
        setError("Temperature data unavailable for this location.");
        return;
      }

      const {
        daily_metrics, weather_strip, record_trend, baseline_summary,
        recent_baseline, past_100_days, seasonal_shift, station_meta,
      } = await dataResponse.json();

      navigate("/results", {
        state: {
          data: {
            cityData,
            dailyMetricsData: daily_metrics,
            weatherStripData: weather_strip,
            recordTrendData: record_trend,
            baselineSummary: baseline_summary,
            recentBaseline: recent_baseline,
            past100Days: past_100_days,
            seasonalShift: seasonal_shift,
            stationMeta: station_meta,
          },
        },
      });
    } catch {
      setError("Something went wrong. Please try again.");
    }
  };

  const handleSelect = (city) => {
    setQuery(`${city.city}, ${city.state}`);
    loadCity(city);
  };

  const handleKeyDown = (e) => {
    if (!suggestions.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, -1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIndex >= 0) {
        handleSelect(suggestions[activeIndex]);
      }
    } else if (e.key === "Escape") {
      setSuggestions([]);
    }
  };

  return (
    <div className="landing-page landing-page--scroll">
      {/* Split hero (title + search beside the U.S. map), then the city cards */}
      <AnomalyMap
        onCitySelect={loadCity}
        middle={<WarmingStripes />}
        hero={
          <>
            <p className="landing-eyebrow">CLIMATE DATA</p>
            <h1 className="landing-title">How Much Hotter?</h1>
            <p className="landing-description">
              See how daily temperatures in your city have shifted since 1951.
            </p>

            <label className="landing-search-label" htmlFor="landing-search">Find your city</label>
            <div className="landing-form">
              <div className="search-combobox">
                <input
                  id="landing-search"
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="City or ZIP code"
                  autoComplete="off"
                  className="landing-input"
                  aria-autocomplete="list"
                  aria-expanded={suggestions.length > 0}
                />
                {suggestions.length > 0 && (
                  <ul className="search-dropdown" ref={listRef} role="listbox">
                    {suggestions.map((city, i) => (
                      <li
                        key={`${city.zip ?? city.city_id}-${city.city}`}
                        role="option"
                        aria-selected={i === activeIndex}
                        className={`search-option ${i === activeIndex ? "search-option--active" : ""}`}
                        onMouseDown={() => handleSelect(city)}
                      >
                        <span className="search-option-city">{city.city}</span>
                        <span className="search-option-meta">
                          {city.state}{city.zip ? ` · ${city.zip}` : ""}
                          {city.zip_city && city.zip_city !== city.city ? ` (${city.zip_city})` : ""}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {nearby && (
              <button type="button" className="landing-nearby" onClick={() => loadCity(nearby)}>
                Your area: <strong>{nearby.city}, {nearby.state}</strong> <span aria-hidden="true">→</span>
              </button>
            )}

            {error && <p className="landing-error">{error}</p>}
          </>
        }
      />
    </div>
  );
}

export default HowMuchHotter;
